import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Megaphone, X, ArrowRight } from 'lucide-react';

const DISMISS_KEY = 'bgn.staking.dismissed_announcements';
const POLL_MS = 60000;

const readDismissed = () => {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}'); }
  catch { return {}; }
};

const writeDismissed = (obj) => {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify(obj)); } catch {}
};

export default function StakingAnnouncementBell() {
  const [pools, setPools] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchPools = useCallback(async () => {
    try {
      const r = await api.get('/staking/pools');
      setPools(r.data.pools || []);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchPools();
    const id = setInterval(fetchPools, POLL_MS);
    // Refresh when window regains focus (admin update case)
    const onFocus = () => fetchPools();
    window.addEventListener('focus', onFocus);
    // Refresh when other tabs update dismissals (e.g., dismiss on /staking page)
    const onStorage = (e) => { if (e.key === DISMISS_KEY) setDismissed(readDismissed()); };
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, [fetchPools]);

  // Also refresh dismissed state when popover opens (in case user dismissed via banner)
  useEffect(() => {
    if (open) setDismissed(readDismissed());
  }, [open]);

  const unread = useMemo(() => {
    return pools.filter(p => {
      if (!p.announcement) return false;
      const k = `${p.pool_id}:${p.announcement_updated_at || ''}`;
      return !dismissed[k];
    });
  }, [pools, dismissed]);

  const dismissOne = (pool) => {
    const k = `${pool.pool_id}:${pool.announcement_updated_at || ''}`;
    const next = { ...dismissed, [k]: true };
    setDismissed(next);
    writeDismissed(next);
  };

  const dismissAll = () => {
    const next = { ...dismissed };
    unread.forEach(p => { next[`${p.pool_id}:${p.announcement_updated_at || ''}`] = true; });
    setDismissed(next);
    writeDismissed(next);
  };

  const goToStaking = (pool) => {
    setOpen(false);
    navigate('/staking');
    // small delay to allow route transition, then scroll to pool card
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="pool-card-${pool.pool_id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
  };

  // Nothing to show? Don't render anything (no dead pixel)
  const hasAnnouncements = pools.some(p => p.announcement);
  if (!hasAnnouncements) return null;

  const count = unread.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 text-[#B7BDC6] hover:text-[#FCD535] transition-colors"
          data-testid="staking-announcement-bell"
          title={count > 0 ? `${count} new staking announcement${count > 1 ? 's' : ''}` : 'Staking announcements'}
        >
          <Megaphone className="w-5 h-5" />
          {count > 0 && (
            <>
              <span className="absolute top-1 right-1 w-2 h-2 bg-[#F6465D] rounded-full animate-pulse" />
              <span className="absolute -top-0.5 -right-0.5 bg-[#F6465D] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-testid="announcement-bell-count">
                {count > 9 ? '9+' : count}
              </span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] p-0 bg-[#181A20] border border-[#2B3139] rounded-sm text-white"
        data-testid="announcement-bell-popover"
      >
        <div className="px-4 py-3 border-b border-[#2B3139] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-[#FCD535]" />
            <div className="text-sm font-semibold">Staking Announcements</div>
            {count > 0 && (
              <span className="text-[9px] bg-[#F6465D] text-white px-1.5 py-0.5 font-mono">{count} NEW</span>
            )}
          </div>
          {count > 0 && (
            <button onClick={dismissAll} className="text-[10px] text-[#848E9C] hover:text-white uppercase tracking-wider" data-testid="bell-dismiss-all">
              Dismiss all
            </button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {unread.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#848E9C]">
              <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <div>You're all caught up.</div>
              <button
                onClick={() => { setOpen(false); navigate('/staking'); }}
                className="mt-3 text-[#FCD535] hover:underline text-xs inline-flex items-center gap-1"
                data-testid="bell-go-staking-empty"
              >
                Browse pools <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ) : unread.map(p => (
            <div key={p.pool_id} className="px-4 py-3 border-b border-[#2B3139]/40 hover:bg-[#2B3139]/20 transition-colors group" data-testid={`bell-item-${p.pool_id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#FCD535] font-semibold">{p.symbol}</span>
                  <span className="text-[#848E9C]">·</span>
                  <span className="font-mono text-[#0ECB81]">{p.apy}% APY</span>
                  <span className="text-[#848E9C]">·</span>
                  <span className="text-[#B7BDC6] truncate">{p.name}</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); dismissOne(p); }}
                  className="text-[#848E9C] hover:text-white opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  title="Dismiss"
                  data-testid={`bell-dismiss-${p.pool_id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-sm text-white leading-snug">{p.announcement}</div>
              <button
                onClick={() => goToStaking(p)}
                className="mt-2 text-[10px] uppercase tracking-wider text-[#FCD535] hover:text-[#F0B90B] inline-flex items-center gap-1 font-mono"
                data-testid={`bell-go-${p.pool_id}`}
              >
                View pool <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
